import { Payload, Platform } from "@/types/profile";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, Settings, Plus, Check, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PayloadConfigPreviewProps {
  payload: Payload | null;
  selectedPlatforms: Platform[];
  isSelected: boolean;
  onSelect: (payloadId: string) => void;
  onConfigure?: (payloadId: string) => void;
}

export const PayloadConfigPreview = ({
  payload,
  selectedPlatforms,
  isSelected,
  onSelect,
  onConfigure,
}: PayloadConfigPreviewProps) => {
  if (!payload) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">Select a Payload</h3>
          <p className="text-sm max-w-sm">
            Choose a payload from the left panel to view its configuration options and details.
          </p>
        </div>
      </div>
    );
  }

  const getPlatformLabel = (platform: Platform): string => {
    const platformLabels: Record<Platform, string> = {
      macos: "macOS",
      ios: "iOS",
      ipados: "iPadOS",
      tvos: "tvOS"
    };
    return platformLabels[platform];
  };

  const getComplexityLevel = (configFieldsCount: number): string => {
    if (configFieldsCount === 0) return "Simple";
    if (configFieldsCount <= 3) return "Medium";
    return "Advanced";
  };

  const getComplexityColor = (configFieldsCount: number): string => {
    if (configFieldsCount === 0) return "text-green-600";
    if (configFieldsCount <= 3) return "text-yellow-600";
    return "text-red-600";
  };

  const isSupported = selectedPlatforms.some(platform => 
    payload.supportedPlatforms.includes(platform)
  );

  const supportedSelectedPlatforms = selectedPlatforms.filter(platform =>
    payload.supportedPlatforms.includes(platform)
  );

  const unsupportedSelectedPlatforms = selectedPlatforms.filter(platform =>
    !payload.supportedPlatforms.includes(platform)
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {payload.name}
              {payload.configFields.length > 0 && (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
            </h2>
            <p className="text-muted-foreground mt-1">{payload.description}</p>
            
            <div className="flex items-center gap-4 mt-3">
              <Badge variant="outline" className="text-xs">
                {payload.category}
              </Badge>
              <span className={cn("text-sm font-medium", getComplexityColor(payload.configFields.length))}>
                {getComplexityLevel(payload.configFields.length)}
              </span>
            </div>
          </div>
          
          <div className="flex gap-2 ml-4">
            {payload.configFields.length > 0 && isSelected && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onConfigure?.(payload.id)}
              >
                <Settings className="h-4 w-4 mr-1" />
                Configure
              </Button>
            )}
            <Button
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => onSelect(payload.id)}
              disabled={!isSupported}
            >
              {isSelected ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Added
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  Add to Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-6">
          {/* Platform compatibility */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Platform Compatibility</h3>
            <div className="space-y-3">
              {supportedSelectedPlatforms.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Supported on your selected platforms:</p>
                  <div className="flex flex-wrap gap-2">
                    {supportedSelectedPlatforms.map(platform => (
                      <Badge key={platform} variant="default" className="text-xs">
                        {getPlatformLabel(platform)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {unsupportedSelectedPlatforms.length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Not supported on: {unsupportedSelectedPlatforms.map(p => getPlatformLabel(p)).join(", ")}
                  </AlertDescription>
                </Alert>
              )}
              
              <div>
                <p className="text-xs text-muted-foreground mb-2">All supported platforms:</p>
                <div className="flex flex-wrap gap-2">
                  {payload.supportedPlatforms.map(platform => (
                    <Badge 
                      key={platform} 
                      variant={selectedPlatforms.includes(platform) ? "default" : "outline"}
                      className="text-xs"
                    >
                      {getPlatformLabel(platform)}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Configuration fields */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              Configuration Options
              <Badge variant="outline" className="text-xs">
                {payload.configFields.length} field{payload.configFields.length !== 1 ? 's' : ''}
              </Badge>
            </h3>
            
            {payload.configFields.length === 0 ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  This payload requires no configuration and can be added directly to your profile.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {payload.configFields.map(field => (
                  <Card key={field.id} className="p-3">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium">{field.name}</h4>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={field.required ? "destructive" : "secondary"}
                            className="text-xs"
                          >
                            {field.required ? "Required" : "Optional"}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {field.type}
                          </Badge>
                        </div>
                      </div>
                      
                      {field.description && (
                        <p className="text-xs text-muted-foreground">{field.description}</p>
                      )}
                      
                      {field.default !== undefined && (
                        <p className="text-xs text-muted-foreground">
                          Default: <span className="font-mono">{String(field.default)}</span>
                        </p>
                      )}
                      
                      {field.options && field.options.length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Options:</p>
                          <div className="flex flex-wrap gap-1">
                            {field.options.map(option => (
                              <Badge key={option} variant="outline" className="text-xs">
                                {option}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
                
                {isSelected && (
                  <Alert>
                    <Settings className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      This payload has been added to your profile. Use the Configure button to set up its options.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};